import type { ReactNode } from "react";
import type { Card, EvidenceKind, PlaceStatus } from "../lib/types";

// The category and place-type values are server-side identifiers and stay English
// in the database; these are the words shown to the player. Verbs throughout, so
// the seven category chips read as one set.
const CATEGORY_LABEL: Record<string, string> = {
  OBSERVE: "BEOBACHTEN",
  CAPTURE: "FESTHALTEN",
  TASTE: "KOSTEN",
  ASK: "FRAGEN",
  MOVE: "GEHEN",
  SENSE: "SPÜREN",
  DARE: "WAGEN",
  CHALLENGE: "HERAUSFORDERUNG",
};

export function categoryLabel(category: string) {
  return CATEGORY_LABEL[category] ?? category;
}

const PLACE_TYPE_LABEL: Record<string, string> = {
  city: "Großstadt",
  town: "Stadt",
  village: "Dorf",
  hamlet: "Weiler",
  municipality: "Gemeinde",
  other: "Ort",
};

export function placeTypeLabel(placeType: string) {
  return PLACE_TYPE_LABEL[placeType] ?? placeType;
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="row" style={{ gap: 10, color: "var(--ink-soft)" }}>
      <span className="spin" aria-hidden />
      <span>{label ?? "Lädt"}</span>
    </div>
  );
}

export function Notice({ children, tone }: { children: ReactNode; tone?: "error" }) {
  return (
    <p className={tone === "error" ? "notice notice--error" : "notice"} role={tone === "error" ? "alert" : undefined}>
      {children}
    </p>
  );
}

const EVIDENCE_LABEL: Record<EvidenceKind, string> = {
  photo: "Foto",
  audio: "Aufnahme",
  note: "Notiz",
  none: "Kein Beleg nötig",
};

export function evidenceLabel(kind: EvidenceKind) {
  return EVIDENCE_LABEL[kind];
}

/** A card in the hand. Whole thing is the tap target -- it is used one-handed. */
export function CardTile({
  card,
  onOpen,
  badge,
}: {
  card: Card;
  onOpen: () => void;
  badge?: string;
}) {
  return (
    <button className={`card cat-${card.category}`} onClick={onOpen}>
      <span className="card__meta">
        <span className="card__category">{categoryLabel(card.category)}</span>
        {badge ? (
          <span className="tiny" style={{ color: "var(--gold)", fontWeight: 700 }}>
            {badge}
          </span>
        ) : null}
        <span className="card__points">{card.points} Pkt</span>
      </span>
      <p className="card__text">{card.text}</p>
      <span className="card__foot">
        <span>{evidenceLabel(card.evidence)}</span>
        {/* The stake belongs on the face of the card: this is the only kind you
            can lose, and you should see that before you pick it up. */}
        {card.failure_penalty > 0 ? (
          <span className="card__stake">Fehlschlag +{card.failure_penalty} aufs Ziel</span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * The Besuch card, pinned above the hand. Shaped like a card because it is one, but
 * it is never dealt, never scored and cannot be thrown away -- so it carries no
 * points chip and says plainly what it is worth.
 */
export function VisitCardTile({ placeName, onOpen }: { placeName: string; onOpen: () => void }) {
  return (
    <button className="card card--pinned cat-VISIT" onClick={onOpen}>
      <span className="card__meta">
        <span className="card__category">BESUCH</span>
        <span className="tiny" style={{ marginLeft: "auto", color: "var(--ink-faint)" }}>
          Keine Punkte
        </span>
      </span>
      <p className="card__text">Warst du wirklich in {placeName}?</p>
      <span className="card__foot">
        <span>Zwei von drei müssen zutreffen</span>
      </span>
    </button>
  );
}

const STATUS_LABEL: Record<PlaceStatus, string> = {
  in_progress: "in Arbeit",
  visited: "Besucht",
  conquered: "Erobert",
};

export function statusLabel(status: PlaceStatus) {
  return STATUS_LABEL[status] ?? status;
}

export function ProgressPanel({
  name,
  earned,
  target,
  baseTarget,
  conquered,
  status,
  children,
}: {
  name: string;
  earned: number;
  target: number;
  baseTarget?: number;
  conquered: boolean;
  status?: PlaceStatus;
  children?: ReactNode;
}) {
  const pct = target > 0 ? Math.min(100, (earned / target) * 100) : 0;
  const inflation = baseTarget !== undefined ? target - baseTarget : 0;

  return (
    <section className="progress">
      <div className="progress__head">
        <h2 className="progress__place">{name}</h2>
        <span className="progress__score">
          {earned}
          <small> / {target}</small>
        </span>
      </div>
      <div
        className="bar"
        role="progressbar"
        aria-valuenow={earned}
        aria-valuemin={0}
        aria-valuemax={target}
        aria-label={`${earned} von ${target} Punkten`}
      >
        <div className="bar__fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="progress__foot">
        {conquered ? (
          <strong style={{ color: "var(--accent)" }}>Erobert</strong>
        ) : (
          <span>
            noch {Math.max(0, target - earned)} Punkte
            {status === "visited" ? " · besucht" : ""}
          </span>
        )}
        {inflation > 0 ? (
          <span className="inflation" title="Durch Abwürfe und Kategorie-Veto hinzugekommen">
            +{inflation} selbst erschwert
          </span>
        ) : null}
        {children}
      </div>
    </section>
  );
}
